// #130 Stok Kontrol katmanı (sağdan açılan drawer) — POST /api/shipmentplans/stock-control
// ile tüm planlı orderId'leri (+ warehouseId) gönderir; ürün bazında
// planlanan / fiziksel stok / eksik tablosu döner. Eksik satırlar için:
//   - Sıfırla        : o ürünün miktarını tüm siparişlerde 0 yapar
//   - Ağırlıklı Dağıt : mevcut stoğu sipariş miktarlarına orantılı paylaştırır
//   - Elle Dağıt      : sipariş bazında düzenlenebilir miktar
// Sonuçlar orderDetailId→newQty ayarlamaları olarak tahta state'ine yansır.
import { useEffect, useMemo, useState } from 'react';
import { s, authHeaders } from '../listPageStyles';
import { API_BASE, unwrap, apiMessage, fmtNumber } from './shipmentPlanningUtils';

export default function StockControlLayer({ orderIds, warehouseId, onClose, onApplyAdjustments, showToast }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [manual, setManual] = useState(null); // {item, allocations:[{orderId,orderDetailId,productName,current,value}]}
  const [detailsByProduct, setDetailsByProduct] = useState(null); // productId -> [{orderId,orderDetailId,productName,current}]

  const load = () => {
    setLoading(true);
    fetch(`${API_BASE}/stock-control`, {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ warehouseId: warehouseId || null, orderIds, adjustments: [] }),
    })
      .then((r) => r.json().then((d) => ({ ok: r.ok, d })))
      .then(({ ok, d }) => {
        if (!ok) throw new Error(apiMessage(d, 'Stok kontrolü başarısız'));
        setItems((unwrap(d)?.items || []));
      })
      .catch((e) => showToast?.('error', e.message))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  // Tüm sipariş kalemlerini bir kez (paralel) çekip ürün bazında önbelleğe al.
  // Böylece her dağıt/sıfırla işleminde tekrar tekrar N istek atılmaz.
  useEffect(() => {
    let alive = true;
    Promise.all((orderIds || []).map((oid) => fetch(`${API_BASE}/order-detail?orderId=${encodeURIComponent(oid)}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => ({ oid, det: unwrap(d) }))
      .catch(() => ({ oid, det: null }))))
      .then((results) => {
        if (!alive) return;
        const map = {};
        results.forEach(({ oid, det }) => {
          (det?.lines || []).forEach((ln) => {
            (map[ln.productId] = map[ln.productId] || []).push({ orderId: oid, orderDetailId: ln.orderDetailId, productName: ln.productName, current: Number(ln.quantity) || 0 });
          });
        });
        setDetailsByProduct(map);
      });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ready = detailsByProduct != null;
  const shortages = useMemo(() => items.filter((it) => (Number(it.shortage) || 0) > 0), [items]);
  const resolveOrderDetails = (productId) => (detailsByProduct?.[productId] || []);

  // Mevcut fiziksel stoğu sipariş miktarlarına orantılı paylaştır (saf hesap).
  const computeWeighted = (item, dets) => {
    const available = Math.max(0, Number(item.physicalStock) || 0);
    const totalReq = dets.reduce((sm, d) => sm + d.current, 0) || 1;
    let allocated = 0;
    return dets.map((d, i) => {
      let q;
      if (i === dets.length - 1) q = Math.max(0, available - allocated); // kalanı sona
      else { q = Math.floor((d.current / totalReq) * available); allocated += q; }
      return { orderDetailId: d.orderDetailId, orderId: d.orderId, newQty: q };
    });
  };

  const zeroOut = (item) => {
    const dets = resolveOrderDetails(item.productId);
    if (!dets.length) { showToast?.('error', 'Kalem bulunamadı.'); return; }
    onApplyAdjustments?.(dets.map((d) => ({ orderDetailId: d.orderDetailId, orderId: d.orderId, newQty: 0 })));
    showToast?.('success', `${item.productName}: tüm miktarlar sıfırlandı.`);
    setItems((prev) => prev.map((it) => it.productId === item.productId ? { ...it, plannedQty: 0, shortage: 0 } : it));
  };

  const weightedDistribute = (item) => {
    const dets = resolveOrderDetails(item.productId);
    if (!dets.length) { showToast?.('error', 'Kalem bulunamadı.'); return; }
    onApplyAdjustments?.(computeWeighted(item, dets));
    const available = Math.max(0, Number(item.physicalStock) || 0);
    showToast?.('success', `${item.productName}: ${fmtNumber(available)} adet orantılı dağıtıldı.`);
    setItems((prev) => prev.map((it) => it.productId === item.productId ? { ...it, plannedQty: available, shortage: 0 } : it));
  };

  // Tüm eksik ürünleri tek seferde stoğa göre orantılı dağıt.
  const solveAll = () => {
    const allAdj = [];
    shortages.forEach((item) => {
      const dets = resolveOrderDetails(item.productId);
      if (dets.length) allAdj.push(...computeWeighted(item, dets));
    });
    if (!allAdj.length) { showToast?.('error', 'Dağıtılacak kalem bulunamadı.'); return; }
    onApplyAdjustments?.(allAdj);
    const fixed = shortages.length;
    setItems((prev) => prev.map((it) => ((Number(it.shortage) || 0) > 0
      ? { ...it, plannedQty: Math.max(0, Number(it.physicalStock) || 0), shortage: 0 }
      : it)));
    showToast?.('success', `${fixed} üründe eksik, fiziksel stoğa göre dağıtıldı.`);
  };

  const openManual = (item) => {
    const dets = resolveOrderDetails(item.productId);
    if (!dets.length) { showToast?.('error', 'Kalem bulunamadı.'); return; }
    setManual({ item, allocations: dets.map((d) => ({ ...d, value: d.current })) });
  };
  const saveManual = () => {
    onApplyAdjustments?.(manual.allocations.map((a) => ({ orderDetailId: a.orderDetailId, orderId: a.orderId, newQty: Number(a.value) || 0 })));
    const sum = manual.allocations.reduce((sm, a) => sm + (Number(a.value) || 0), 0);
    setItems((prev) => prev.map((it) => it.productId === manual.item.productId ? { ...it, plannedQty: sum, shortage: Math.max(0, sum - (Number(it.physicalStock) || 0)) } : it));
    showToast?.('success', `${manual.item.productName}: miktarlar güncellendi.`);
    setManual(null);
  };

  return (
    <div style={{ ...s.overlay, justifyContent: 'flex-end', alignItems: 'stretch' }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(720px, 96vw)', height: '100%', background: 'var(--bg-card,#fff)',
          display: 'flex', flexDirection: 'column', boxShadow: '-12px 0 40px rgba(15,23,42,0.25)',
          animation: 'panel-in 220ms ease both',
        }}>
        <div style={{ ...s.modalHeader, borderRadius: 0 }}>
          <div>
            <h3 style={s.modalTitle}>📦 Stok Kontrol</h3>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-secondary,#6b7280)' }}>
              Planlanan {orderIds.length} sipariş için depo stok yeterlilik kontrolü.
            </p>
          </div>
          <button style={s.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 20px', borderBottom: '1px solid var(--border-color,#eef2f7)' }}>
          <button style={s.btnSecondary} onClick={load} disabled={loading}>Yenile</button>
          {shortages.length > 0 && (
            <button style={{ ...s.btnPrimary, opacity: ready ? 1 : 0.55, cursor: ready ? 'pointer' : 'not-allowed' }} disabled={!ready} onClick={solveAll}
              title="Tüm eksik ürünleri fiziksel stoğa göre orantılı dağıt">
              ⚡ Tüm Eksikleri Dağıt
            </button>
          )}
          <span style={{ marginLeft: 'auto', alignSelf: 'center', fontSize: 12.5, fontWeight: 700, color: shortages.length ? 'var(--danger)' : '#16a34a' }}>
            {loading ? 'Kontrol ediliyor…' : !ready ? 'Kalemler hazırlanıyor…' : shortages.length ? `${shortages.length} üründe eksik` : 'Tüm ürünlerde stok yeterli'}
          </span>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary,#94a3b8)' }}>Yükleniyor…</div>
          ) : items.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary,#94a3b8)' }}>Kontrol edilecek kalem yok.</div>
          ) : (
            <div style={{ border: '1px solid var(--border-color,#e5e7eb)', borderRadius: 12, overflow: 'hidden' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr 1.7fr', gap: 6, padding: '10px 12px', background: 'var(--grid-canvas,#f9fafb)', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: 'var(--text-secondary,#6b7280)' }}>
                <div>Ürün</div>
                <div style={{ textAlign: 'right' }}>Planlanan</div>
                <div style={{ textAlign: 'right' }}>Fiziksel</div>
                <div style={{ textAlign: 'right' }}>Eksik</div>
                <div style={{ textAlign: 'center' }}>İşlem</div>
              </div>
              {items.map((it, i) => {
                const short = (Number(it.shortage) || 0) > 0;
                return (
                  <div key={it.productId || i} style={{
                    display: 'grid', gridTemplateColumns: '2.2fr 1fr 1fr 1fr 1.7fr', gap: 6, padding: '10px 12px',
                    borderTop: '1px solid var(--border-color,#eef2f7)', fontSize: 13, alignItems: 'center',
                    background: short ? 'rgba(226,68,92,0.04)' : 'transparent',
                  }}>
                    <div style={{ fontWeight: 600 }}>{it.productName}<span style={{ color: 'var(--text-secondary,#94a3b8)', fontWeight: 500 }}>{it.unitName ? ` (${it.unitName})` : ''}</span></div>
                    <div style={{ textAlign: 'right' }}>{fmtNumber(it.plannedQty)}</div>
                    <div style={{ textAlign: 'right' }}>{fmtNumber(it.physicalStock)}</div>
                    <div style={{ textAlign: 'right', fontWeight: 800, color: short ? 'var(--danger)' : '#16a34a' }}>{short ? fmtNumber(it.shortage) : '—'}</div>
                    <div style={{ display: 'flex', gap: 4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {short ? (
                        <>
                          <button style={miniBtn('#64748b', !ready)} disabled={!ready} onClick={() => zeroOut(it)}>Sıfırla</button>
                          <button style={miniBtn('#0073ea', !ready)} disabled={!ready} onClick={() => weightedDistribute(it)}>Ağırlıklı</button>
                          <button style={miniBtn('#7a3cff', !ready)} disabled={!ready} onClick={() => openManual(it)}>Elle</button>
                        </>
                      ) : <span style={{ color: '#16a34a', fontWeight: 700, fontSize: 12 }}>✓ Yeterli</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {manual && (
        <div style={s.overlay} onClick={() => setManual(null)}>
          <div style={{ ...s.modal, maxWidth: 560, width: '94%' }} onClick={(e) => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h3 style={s.modalTitle}>Elle Dağıt — {manual.item.productName}</h3>
              <button style={s.closeBtn} onClick={() => setManual(null)}>×</button>
            </div>
            <div style={s.modalBody}>
              <div style={{ fontSize: 12.5, color: 'var(--text-secondary,#64748b)', marginBottom: 4 }}>
                Fiziksel stok: <b>{fmtNumber(manual.item.physicalStock)}</b>
              </div>
              {manual.allocations.map((a, idx) => (
                <div key={a.orderDetailId || idx} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ flex: 1, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {a.productName} <span style={{ color: '#94a3b8' }}>(istenen {fmtNumber(a.current)})</span>
                  </span>
                  <input type="number" min="0" value={a.value}
                    onChange={(e) => setManual((m) => ({ ...m, allocations: m.allocations.map((x, i) => i === idx ? { ...x, value: e.target.value } : x) }))}
                    style={{ width: 110, padding: '7px 10px', borderRadius: 10, border: '1px solid var(--border-color,#cbd5e1)', fontSize: 13, textAlign: 'right' }} />
                </div>
              ))}
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSecondary} onClick={() => setManual(null)}>Vazgeç</button>
              <button style={s.btnPrimary} onClick={saveManual}>Uygula</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function miniBtn(color, disabled) {
  return {
    padding: '4px 9px', borderRadius: 8, border: `1px solid ${color}55`, background: `${color}14`,
    color, fontWeight: 800, fontSize: 11.5, cursor: disabled ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap',
    opacity: disabled ? 0.5 : 1,
  };
}
